/**
 * Chord quality recognition — "What kind of chord is this?"
 *
 * Plays the chord twice: once as a rolled arpeggio (so each note is audible),
 * then once as a block. Distractors are similar-quality chords drawn from
 * the same group as the target (triads, sevenths, 9ths, 11/13ths, altered
 * dominants), with a preference for chords sharing the same root family —
 * e.g. for `maj9`, the closest distractor is `maj7`, not `dim7`.
 *
 * For 5+ note chords, the block-chord voicing doubles the root in the bass
 * register (octave below) so the upper extensions sit clearly in the treble
 * and their color registers — close root position alone tends to bury the
 * 9/11/13 inside the chord cluster.
 */

import { AudioPlanBuilder, type AudioPlan } from "../audio-plan.js";
import type { Exercise, AnswerOption } from "../exercise.js";
import { Rng } from "../exercise.js";
import { buildChord } from "../utils.js";

const QUALITY_LABELS: Record<string, string> = {
  // Triads
  maj: "Major",
  min: "Minor",
  dim: "Diminished",
  aug: "Augmented",
  // Sevenths
  maj7: "Major 7",
  min7: "Minor 7",
  dom7: "Dominant 7",
  min7b5: "Half-diminished (m7♭5)",
  dim7: "Fully diminished (°7)",
  // 9th extensions
  maj9: "Major 9",
  min9: "Minor 9",
  dom9: "Dominant 9",
  // 11/13 extensions
  maj11: "Major 11",
  min11: "Minor 11",
  dom13: "Dominant 13",
  // Altered dominants
  dom7b9: "Dominant 7♭9",
  dom7s9: "Dominant 7♯9",
  dom7s11: "Dominant 7♯11",
  dom7b13: "Dominant 7♭13",
};

const TRIAD_GROUP = ["maj", "min", "dim", "aug"];
const SEVENTH_GROUP = ["maj7", "min7", "dom7", "min7b5", "dim7"];
const EXT9_GROUP = ["maj9", "min9", "dom9"];
const EXT11_13_GROUP = ["maj11", "min11", "dom13"];
const ALT_DOM_GROUP = ["dom7b9", "dom7s9", "dom7s11", "dom7b13"];

/**
 * Pool of plausible distractors for a given target quality. Stays within
 * the broader extension family but pulls in the parent seventh of the
 * same root — that's what makes the choice pedagogically interesting
 * (e.g. is this a bare maj7 or a maj9?).
 */
function getDistractorGroup(quality: string): string[] {
  if (TRIAD_GROUP.includes(quality)) return TRIAD_GROUP;
  if (SEVENTH_GROUP.includes(quality)) return SEVENTH_GROUP;
  if (EXT9_GROUP.includes(quality)) return [...EXT9_GROUP, "maj7", "min7", "dom7"];
  if (EXT11_13_GROUP.includes(quality)) return [...EXT11_13_GROUP, ...EXT9_GROUP];
  if (ALT_DOM_GROUP.includes(quality)) return [...ALT_DOM_GROUP, "dom7", "dom9"];
  return [];
}

/**
 * Coarse "root family" — the dimension along which extensions of the
 * same quality cluster. maj7 / maj9 / maj11 all share root family
 * "maj"; dom7 and all altered dominants share "dom"; min7b5 is its
 * own family because hearing it beside a min7 is a different
 * confusion than min7 vs maj7.
 */
function rootFamily(q: string): string {
  if (q === "min7b5") return "min7b5";
  if (q.startsWith("dim")) return "dim";
  if (q.startsWith("aug")) return "aug";
  if (q.startsWith("maj")) return "maj";
  if (q.startsWith("dom")) return "dom";
  if (q.startsWith("min")) return "min";
  return q;
}

/**
 * Pick distractors with a same-root bias. Up to 2 distractors come from
 * the target's root family (e.g. maj9 → maj7 first), then the rest
 * come from the broader group. Total picked = `count`.
 */
function pickDistractors(quality: string, rng: Rng, count: number): string[] {
  const group = getDistractorGroup(quality);
  const targetFamily = rootFamily(quality);
  const sameRoot = group.filter((q) => q !== quality && rootFamily(q) === targetFamily);
  const otherRoot = group.filter((q) => q !== quality && rootFamily(q) !== targetFamily);

  const sameRootShuffled = rng.shuffle(sameRoot);
  const otherRootShuffled = rng.shuffle(otherRoot);

  const sameRootQuota = Math.min(2, sameRootShuffled.length, count);
  const fromSameRoot = sameRootShuffled.slice(0, sameRootQuota);
  const remaining = count - fromSameRoot.length;
  const fromOtherRoot = otherRootShuffled.slice(0, remaining);

  return [...fromSameRoot, ...fromOtherRoot];
}

export interface ChordQualityParams {
  quality: string;
}

export function generateChordQuality(
  itemId: string,
  params: ChordQualityParams,
  seed: number,
): Exercise {
  const rng = new Rng(seed);
  // Random root somewhere comfortable, ignoring the key (chord quality is context-free).
  // Extended chords sound better with a slightly lower root so the upper
  // extensions land in midrange rather than running off the top.
  const isExtended = isExtendedQuality(params.quality);
  const rootMidi = isExtended ? 50 + rng.int(6) : 55 + rng.int(8);
  const chord = buildChord(rootMidi, params.quality);
  const blockVoicing = voicedBlockChord(chord);

  const builder = new AudioPlanBuilder();
  // Arpeggio (low to high): each note for ~0.4s, slightly overlapping.
  // Use the close-position chord so the listener hears each chord tone
  // as a discrete pitch in stack order — including the upper extensions.
  for (const note of chord) {
    builder.addNote(note, 0.55, 0.20, "melody");
    builder.advance(0.30);
  }
  builder.advance(0.4);
  // Block chord: voicing-aware for extended chords so upper extensions sit
  // above the shell rather than getting buried inside it.
  // Slightly lower per-note gain on the block when there are many notes,
  // so the chord doesn't become a wall of sound.
  const blockGain = blockVoicing.length >= 6 ? 0.10 : 0.13;
  builder.addChord(blockVoicing, 1.5, blockGain, "harmony");
  builder.advance(1.6);
  const audio = builder.build();

  // Distractor count: 3 for triads (4 options total), 4 for everything else (5 options).
  const distractorCount = TRIAD_GROUP.includes(params.quality) ? 3 : 4;
  const distractors = pickDistractors(params.quality, rng, distractorCount);
  const allQualities = rng.shuffle([params.quality, ...distractors]);

  const options: AnswerOption[] = allQualities.map((q) => ({
    id: q,
    label: QUALITY_LABELS[q] ?? q,
    sublabel: q,
  }));

  return {
    itemId,
    exerciseType: "chord-quality-recognition",
    audio,
    prompt: "What chord quality did you hear?",
    options,
    correctAnswerId: params.quality,
    meta: {
      seed,
      explanation: `${QUALITY_LABELS[params.quality] ?? params.quality} chord (${params.quality}). Notes: ${chord.map((m) => midiToShortName(m)).join(", ")}.`,
      distractorCount: options.length - 1,
      teachAudio: buildChordQualityTeachPlan(chord, blockVoicing),
    },
  };
}

/**
 * True for any 5+ note chord (9ths, 11/13ths, altered dominants).
 * These get the open block-chord voicing.
 */
function isExtendedQuality(quality: string): boolean {
  return (
    EXT9_GROUP.includes(quality) ||
    EXT11_13_GROUP.includes(quality) ||
    ALT_DOM_GROUP.includes(quality)
  );
}

/**
 * For 5+ note chords, double the root an octave below the close-position
 * chord. The bass anchors the root in its natural register; the close
 * chord sits in midrange-treble where the upper extensions can be heard
 * as color tones rather than dissonances buried in a cluster.
 *
 * For ≤4 notes (triads, sevenths), returns the chord unchanged — close
 * root position already sounds clean.
 */
function voicedBlockChord(close: number[]): number[] {
  if (close.length <= 4) return close;
  const root = close[0]!;
  return [root - 12, ...close];
}

/**
 * Wrong-answer teach plan for chord quality: a slower arpeggio so each
 * chord tone is hearable as a discrete pitch from the root, followed by
 * a sustained block chord. The original audio uses a quicker arpeggio
 * (0.30s steps); this one nearly triples the per-note hold so beginners
 * can isolate the third's quality (the maj/min telling note) and any
 * upper-extension color.
 */
function buildChordQualityTeachPlan(chord: number[], blockVoicing: number[]): AudioPlan {
  const builder = new AudioPlanBuilder();
  for (const note of chord) {
    builder.addNote(note, 0.9, 0.22, "melody");
    builder.advance(1.0);
  }
  builder.advance(0.3);
  const blockGain = blockVoicing.length >= 6 ? 0.10 : 0.13;
  builder.addChord(blockVoicing, 1.8, blockGain, "harmony");
  builder.advance(1.9);
  return builder.build();
}

function midiToShortName(midi: number): string {
  const names = ["C", "C♯", "D", "E♭", "E", "F", "F♯", "G", "A♭", "A", "B♭", "B"];
  return names[((midi % 12) + 12) % 12]!;
}
